import { scry, scryImageUrl } from "../lib/scryClient";
import { toast } from "@/components/ui/use-toast";
// web/src/components/QuickOracleModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ScryfallCard } from "@/types/cards";
import {
  getCardById,
  getRulingsByCardId,
  type ScryfallRuling,
} from "@/lib/scryfall";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PriceStrip from "@/components/PriceStrip";

type QuickOracleCard = {
  name: string;
  scryfall_id?: string | null;
  image?: string | null;
  set?: string | null;
  collector_number?: string | null;
  oracle_text?: string | null;
  prices?: { usd?: string | number | null; usd_foil?: string | number | null } | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: QuickOracleCard | null;
};

function scryfallImageFromId(id?: string | null): string | null {
  if (!id) return null;
  return ` + scryImageUrl(id, "large") + `;
}

export default function QuickOracleModal(
  props: Partial<Props> = {},
) {
  const open = props.open ?? true;
  const onOpenChange = props.onOpenChange;
  const card = props.card ?? null;

  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();

  const [sfCard, setSfCard] = useState<ScryfallCard | null>(null);
  const [rulings, setRulings] = useState<ScryfallRuling[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const acRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!cardId) return;
    toast({
      description: null,
    });
    setPending(true);

    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;

    Promise.all([
      getCardById(cardId, ac.signal),
      getRulingsByCardId(cardId, ac.signal).catch(() => [] as ScryfallRuling[]),
    ])
      .then(([c, r]) => {
        setSfCard(c);
        setRulings(Array.isArray(r) ? r : []);
      })
      .catch((e) =>
        toast({
          description: e?.message || "Failed to load card details",
        }),
      )
      .finally(() => {
        if (acRef.current === ac) setPending(false);
      });

    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") navigate(-1);
    }
    window.addEventListener("keydown", onEsc);
    return () => {
      ac.abort();
      window.removeEventListener("keydown", onEsc);
    };
  }, [cardId, navigate]);

  function onBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) navigate(-1);
  }

  function imgUrl(c: ScryfallCard | null): string {
    if (!c) return "";
    const face = c.card_faces?.[0]?.image_uris;
    return (
      face?.normal ||
      c.image_uris?.normal ||
      face?.large ||
      c.image_uris?.large ||
      face?.small ||
      c.image_uris?.small ||
      ""
    );
  }

  if (!cardId) return null;

  const Legality = ({ label, val }: { label: string; val?: string }) => (
    <div className="flex items-center justify-between rounded border border-white/10 bg-white/5 px-2 py-1 text-xs">
      <span className="text-white/90">{label}</span>
      <span
        className={
          val === "legal"
            ? "text-emerald-300"
            : val === "restricted"
              ? "text-amber-300"
              : "text-red-300"
        }
      >
        {val ?? "—"}
      </span>
    </div>
  );

  const close = () => {
    if (typeof onOpenChange === "function") onOpenChange(false);
    else navigate(-1);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center md:p-4"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div className="fixed inset-0 bg-black/70" aria-hidden />
      <div
        className="relative z-[101] w-full md:w-[min(95vw,900px)] h-[100dvh] md:h-auto max-h-[100dvh] md:max-h-[90vh]
                   overflow-hidden md:rounded-2xl rounded-none border border-slate-700 bg-gray-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-gray-950/90 backdrop-blur">
          <div className="text-base md:text-lg font-semibold text-white truncate">
            {sfCard?.name || "Card details"}
          </div>
          <button
            type="button"
            className="px-3 py-1 rounded-md border border-white/10 hover:bg-white/5 text-white text-sm"
            onClick={close}
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="bg-black p-0 md:p-3">
              <div className="w-full aspect-[3/4] bg-black overflow-hidden md:rounded-lg">
                <img
                  src={imgUrl(sfCard)}
                  alt={sfCard?.name || "Card"}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm text-slate-300">
                {(sfCard?.set || "")?.toUpperCase?.() || ""}
                {sfCard?.collector_number ? ` • #${sfCard.collector_number}` : ""}
              </div>
              {sfCard?.oracle_text ? (
                <div className="whitespace-pre-line text-sm text-slate-200">{sfCard.oracle_text}</div>
              ) : null}
              {sfCard?.prices ? (
                <div className="text-sm text-slate-300">
                  {sfCard?.prices?.usd ? `Price: $${Number(sfCard.prices.usd).toFixed(2)}` : ""}
                  {sfCard?.prices?.usd_foil ? ` • Foil: $${Number(sfCard.prices.usd_foil).toFixed(2)}` : ""}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


